import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store"; // Assuming your store file is named 'store.ts'
import { Button, Image, Input, Popconfirm, Space } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  AppstoreOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { deleteData, fetchData, postData, putData } from "../store/actions/cms";
import { useEffect, useState } from "react";
import { PATH_EXPENSE as PATH, PATH_MANAGE } from "../config";
import CustomPaginatedTable from "../components/CustomPaginatedTable";

import { isArray } from "lodash";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import ModalInventory from "../components/modal/ModalInventory";
import PageHeader from "../components/PageHeader";
import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";
import { useLocation, useNavigate } from "react-router-dom";
import useSocket from "../hooks/useSocket";
import { useLocalState } from "../hooks/useLocalStorage";

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

// Dummy API functions - Replace these with actual API endpoints
const markSoldApi = async (data: { scan_code: string }) => {
  return queryApi(`ledger/scan/sold?is_sold=1`, "POST", {
    ...data,
    query: "is_sold=1",
  });
};

const deleteInventoryApi = async (data: { slot_id: string }) => {
  return queryApi(`slot/inventory/${data.slot_id}`, "PUT", data);
};

const ConfigurationPage: React.FC<configurationPageProps> = ({}) => {
  const [showDrawer, setShowDrawer] = useState(false);
  const [viewMode, setViewMode] = useLocalState(VIEW_MODE_STORAGE_KEY, "card");
  const { sendMessage } = useSocket();

  const [filterData, setFilterData] = useState({});
  const [amount, setAmount] = useState("");
  const [stats, setStats] = useState<any>({ total: 0, available: 0 });
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.openModal) {
      updateDrawer(true);
    }
  }, [location?.state?.openModal]);

  // Mutation for marking sold
  const { mutate: markSold, isPending: isMarkingSold } = useMutation({
    mutationFn: (data: { scan_code: string }) => markSoldApi(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["slot/inventory"],
      });
      sendMessage("screen-refresh");
      triggerNotification({
        title: "Success",
        description: "Item marked as sold successfully",
        type: "success",
      });
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to mark item as sold",
        type: "error",
      });
    },
  });

  const onMarkSold = (record: any) => {
    markSold({ scan_code: record.activated_code });
  };

  // Mutation for deleting inventory
  const { mutate: deleteInventory, isPending: isDeleting } = useMutation({
    mutationFn: (data: any) => deleteInventoryApi(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["slot/inventory"],
      });
      triggerNotification({
        title: "Success",
        description: "Item deleted successfully",
        type: "success",
      });
    },
    onError: (error) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to delete item",
        type: "error",
      });
    },
  });

  const onDelete = (record: any) => {
    deleteInventory({ ...record, status: !record.status });
  };

  const customRender = (item: any, index: number, globalIndex: number) => {
    const totalValue = item.amount * item.pack_size;
    return (
      <div
        key={item.scan_code || index}
        className="app-surface rounded-xl shadow-sm border border-solid app-border p-6 hover:shadow-md transition-shadow duration-200 mb-4"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4 flex-1">
            {item.image_url && (
              <Image
                width={80}
                height={80}
                src={item.image_url?.replace("original", "original")}
                className="rounded-lg object-cover"
                // preview={false}
              />
            )}
            <div className="flex-1">
              <h3 className="text-lg font-semibold app-text mb-1">
                {item.name}
              </h3>
              <p className="text-sm app-muted">
                Game #{item.game_number} • Pack #{item.pack}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm app-muted">Value</p>
            <p className="text-lg font-semibold"
            style={{ color: "var(--dash-value)" }}>
              {item.currency}
              {item.amount}
            </p>
          </div>
          <div>
            <p className="text-sm app-muted">
              Pack Size
            </p>
            <p className="text-lg font-semibold app-text">
              {item.pack_size}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <p className="text-sm app-muted">
              Total Value
            </p>
            <p className="text-lg font-semibold text-blue-600">
              {item.currency}
              {totalValue}
            </p>
          </div>
          <div>
            <p className="text-sm app-muted">
              Date Added
            </p>
            <p className="text-sm font-medium app-text">
              {moment(item.created_at).format("MMMM DD, YYYY")}
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4 border-0 border-t border-solid app-border">
          {item.is_sold || item.is_closed ? (
            <span className="text-sm font-medium app-muted">
              {item.is_sold ? "Sold" : "Discontinued"}
            </span>
          ) : (
            <Popconfirm
              title="Are you sure to mark this sold?"
              onConfirm={() => onMarkSold(item)}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <div className="text-red-600 hover:text-red-700 text-sm font-medium">
                Mark Sold
              </div>
            </Popconfirm>
          )}
          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => onDelete(item)}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <div className="flex items-center space-x-1 text-red-600 hover:text-red-700 text-sm font-medium">
              <DeleteOutlined className="text-base" />
            </div>
          </Popconfirm>
        </div>
      </div>
    );
  };

  const getDataStats = (data: any[]) => {
    if (isArray(data)) {
      let total = 0;
      let available = data.length;

      total = data.reduce(
        (value, item) => value + item.amount * item.pack_size,
        0,
      );

      setStats({ total, available });
    }
  };
  const updateDrawer = (state: boolean = false) => {
    setShowDrawer(state);
    if (!state)
      navigate(location.pathname, {
        replace: true,
        state: null,
      });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "game_number",
      key: "game_number",
      render: (item, record, index) => <div>{index + 1}</div>,
    },
    {
      title: "Image",
      dataIndex: "image_url",
      key: "image_url",
      render: (image_url) => (
        <Image
          width={40}
          height={40}
          src={image_url?.replace("original", "original")}
        />
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Game-Pack #</div>,
      dataIndex: "game_number",
      key: "game_number",
      width: 120,
      sorter: (a, b) =>
        (a.game_number ?? 0) - (b.game_number ?? 0) ||
        (a.pack ?? 0) - (b.pack ?? 0),
      render: (item, record, index) => (
        <div style={{ minWidth: "max-content" }}>
          {record.game_number}-{record.pack}
        </div>
      ),
    },
    // {
    //   title: "Scan Code",
    //   dataIndex: "activated_code",
    //   key: "activated_code",
    // },

    {
      title: "Value",
      dataIndex: "amount",
      key: "amount",
      sorter: (a, b) => (a.amount ?? 0) - (b.amount ?? 0),
      render: (item, record, index) => (
        <div>
          {record.currency}
          {record.amount}
        </div>
      ),
      width: 60,
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Pack Size</div>,
      dataIndex: "pack_size",
      key: "pack_size",
      width: 100,
      sorter: (a, b) => (a.pack_size ?? 0) - (b.pack_size ?? 0),
    },
    {
      title: <div style={{ minWidth: "max-content" }}>Date Added</div>,
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a, b) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (item, record, index) => (
        <div>{moment(item).format("MMMM DD, YYYY")}</div>
      ),
    },
    {
      title: "Custom Actions",
      key: "is_sold",
      render: (is_sold, record, index) => (
        <Space size="middle">
          {record.is_sold || record.is_closed ? (
            <div>{record.is_sold ? "Sold" : "Discontinued"}</div>
          ) : (
            <Popconfirm
              title="Are you sure to mark this sold?"
              onConfirm={() => onMarkSold(record)}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <a href="#" style={{ color: "red" }}>
                Mark Sold
              </a>
            </Popconfirm>
          )}
        </Space>
      ),
    },

    {
      title: "Action",
      key: "action",
      render: (record) => (
        <Space size="middle">
          {/* <a onClick={() => onActionItem(record)}>Edit</a> */}
          <Popconfirm
            title="Are you sure to delete this?"
            onConfirm={() => onDelete(record)}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            {/* <a href="#" style={{ color: "red" }}>
              Delete
            </a> */}
            <DeleteOutlined style={{ fontSize: 22, color: "red" }} />
          </Popconfirm>
        </Space>
      ),
    },
  ];
  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      <PageHeader
        title={`Inventory ( ${stats?.total} )`}
        subTitle={`Total pack : ${stats?.available}`}
        extra={[
          <Input
            key="search"
            allowClear
            style={{ width: "300px" }}
            defaultValue=""
            placeholder="Search with Name, Game# or Pack#"
            onChange={(e) => {
              console.log(e.target.value);
              setAmount(e.target.value);
            }}
          />,

          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              navigate(location.pathname, {
                replace: true,
                state: { openModal: true },
              })
            }
          >
            Add
          </Button>,
          <Button.Group key="view-toggle">
            <Button
              type={viewMode === "table" ? "primary" : "default"}
              icon={<TableOutlined />}
              onClick={() => setViewMode("table")}
            >
              Table
            </Button>
            <Button
              type={viewMode === "card" ? "primary" : "default"}
              icon={<AppstoreOutlined />}
              onClick={() => setViewMode("card")}
            >
              Card
            </Button>
          </Button.Group>,
        ]}
      />

      {/* <div className=" bg-gray-800/90 backdrop-blur-xl rounded-xl shadow-sm border app-border overflow-hidden flex flex-col flex-1 p-6"> */}
      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        hidePagination={true}
        filterData={filterData}
        customDataMapper={(data) =>
          data.map((item: any) => ({ ...item.lottery, ...item }))
        }
        localFilterFunction={(data) => {
          return data?.filter(
            (item) =>
              (("" + item.game_number).includes(amount) ||
                item.name?.toLowerCase().includes(amount?.toLowerCase()) ||
                ("" + item.pack).includes(amount)) ??
              [],
          );
        }}
        getDataStats={getDataStats}
        queryKey={["slot/inventory", { type: "all" }]}
        path={"slot/inventory"}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />
      {/* </div> */}

      <ModalInventory
        showModal={showDrawer}
        toggleModal={updateDrawer}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ["slot/inventory"],
          });
        }}
      />

      {/* Expenses Table */}
      {/* <div className="app-surface rounded-lg shadow-sm border app-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="app-surface-alt border-b app-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Vehicle
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium app-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="app-surface divide-y divide-gray-200">
              {expenses.map((expense) => (
                <tr key={expense.id} className="app-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    {new Date(expense.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-text">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 app-muted" />
                      {expense.vehicle}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getCategoryColor(
                        expense.category
                      )}`}
                    >
                      {getCategoryIcon(expense.category)}
                      {expense.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm app-text max-w-xs truncate">
                    {expense.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold app-text">
                    ${expense.amount.toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    {expense.receipt ? (
                      <span className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer">
                        View Receipt
                      </span>
                    ) : (
                      <span className="app-muted">No receipt</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm app-muted">
                    <div className="flex items-center gap-2">
                      <button
                        // onClick={() => handleEdit(expense)}
                        className="text-blue-600 hover:text-blue-900 transition-colors"
                      >
                        <Icon name="Edit2" className="w-4 h-4" />
                      </button>
                      <button
                        // onClick={() => handleDelete(expense.id)}
                        className="text-red-600 hover:text-red-900 transition-colors"
                      >
                        <Icon name="Trash2" className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div> */}

      {/* Add/Edit Modal */}
      {/* {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="app-surface rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold app-text mb-4">
                {editingExpense ? "Edit Expense" : "Add New Expense"}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Vehicle
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.vehicle}
                    onChange={(e) =>
                      setFormData({ ...formData, vehicle: e.target.value })
                    }
                  >
                    <option value="">Select Vehicle</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle} value={vehicle}>
                        {vehicle}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Category
                  </label>
                  <select
                    required
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.category}
                    onChange={(e) =>
                      setFormData({ ...formData, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Enter expense description"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Amount ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    placeholder="0.00"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.amount}
                    onChange={(e) =>
                      setFormData({ ...formData, amount: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium app-text mb-1">
                    Receipt (optional)
                  </label>
                  <input
                    type="text"
                    placeholder="Receipt filename or reference"
                    className="w-full px-3 py-2 border app-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={formData.receipt}
                    onChange={(e) =>
                      setFormData({ ...formData, receipt: e.target.value })
                    }
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 app-text app-surface-alt border app-border rounded-lg app-hover transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingExpense ? "Update" : "Add"} Expense
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  data: state.cms?.[PAGE_TYPE],
  storeId: state.user?.profileData?.storeId,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = (dispatch: any) => ({
  // login: (data: any) => dispatch(getProfile(data)),
  fetchCms: (reqData: any) =>
    dispatch(fetchData(PATH_MANAGE, PATH, reqData, PAGE_TYPE)),
  generateToken: (id: any) =>
    dispatch(fetchData(PATH_MANAGE, `${PATH}/login/app/${id}`, {}, "")),
  createData: (reqData: any) =>
    dispatch(postData(PATH_MANAGE, `${PATH}`, reqData, PAGE_TYPE)),

  editData: (id: string, reqData: any) =>
    dispatch(putData(PATH_MANAGE, `${PATH}/`, id, reqData, PAGE_TYPE)),
  removeCms: (reqData: any) =>
    dispatch(deleteData(PATH_MANAGE, `${PATH}/`, reqData, PAGE_TYPE)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
